<template>
  <div>
    <!-- Header -->
    <div v-if="showHeader" class="flex items-center justify-between mb-4">
      <Heading level="h5">{{ title }}</Heading>
      <div class="flex items-center gap-2">
        <!-- Toggle Filter Button with Badge (First) -->
        <div v-if="showFilters" class="relative inline-flex">
          <HLButton
            @click="handleToggle"
            :variant="toggleVariant"
            size="md"
            :disabled="disabled"
            :aria-label="expanded ? t('reports.filter.hide') : t('reports.filter.show')"
            :aria-expanded="expanded"
            :aria-controls="`filter-panel-${uniqueId}`"
          >
            <Icon name="filter" size="sm" class="mr-2" />
            {{ expanded ? t('reports.filter.hide') : t('reports.filter.show') }}
          </HLButton>
          <!-- Active Filter Count Badge (Round) -->
          <span 
            v-if="hasActiveFilters" 
            class="absolute -top-1.5 -right-1.5 w-5 h-5 flex items-center justify-center text-[10px] font-bold text-white bg-blue-500 rounded-full shadow-md ring-2 ring-white dark:ring-gray-800 z-10"
          >
            {{ activeFilterCount }}
          </span>
        </div>
        
        <!-- Custom Action Buttons (slot) -->
        <!-- Provide default button props so consumers can use v-slot:actions="{ defaults }" -->
        <slot name="actions" :defaults="defaultActionButtonProps"></slot>
        
        <!-- Custom Action Buttons (prop-based) -->
        <HLButton
          v-for="(button, index) in actionButtons"
          :key="index"
          @click="button.onClick"
          :variant="mergeButton(button).variant"
          :size="mergeButton(button).size"
          :disabled="mergeButton(button).disabled"
          :aria-label="mergeButton(button).ariaLabel || mergeButton(button).label"
        >
          <Icon 
            v-if="mergeButton(button).icon"
            :name="mergeButton(button).icon"
            size="sm"
            class="mr-2"
            :class="{ 'animate-spin': mergeButton(button).loading }"
          />
          {{ mergeButton(button).label }}
        </HLButton>
        
        <!-- Export Button -->
        <HLButton
          v-if="showExport"
          @click="handleExport"
          :variant="exportVariant"
          size="md"
          class="export-btn"
          :aria-label="t('reports.exportSection', { section: title })"
        >
          <Icon name="download" size="sm" class="mr-2" />
          {{ exportText }}
        </HLButton>
      </div>
    </div>

    <!-- Filter Panel -->
    <Transition name="filter-slide">
      <div 
        v-show="expanded" 
        :id="`filter-panel-${uniqueId}`"
        :class="[filterColors.background, filterColors.border, 'rounded-lg shadow-md p-4 mb-4 border']"
        role="region"
        :aria-label="t('reports.filter.title')"
      >
        <!-- Filter Grid -->
        <div :class="gridClasses">
          <!-- Search Input -->
          <div v-if="showSearch" :class="searchColSpan">
            <HLInput
              v-model="searchValue"
              type="text"
              :placeholder="searchPlaceholder || t('reports.filter.search', { section: title.toLowerCase() })"
              :aria-label="searchPlaceholder || t('reports.filter.searchPlaceholder')"
              clearable
            >
              <template #prefix>
                <Icon name="search" size="sm" />
              </template>
            </HLInput>
          </div>

          <!-- Slot for custom filters -->
          <slot></slot>
        </div>

        <!-- Include Deleted Items Checkbox -->
        <div v-if="showDeletedToggle" class="mt-3 flex items-center gap-2" style="pointer-events: auto; position: relative; z-index: 10;" @click.stop>
          <HLCheckbox
            id="include-deleted-checkbox"
            v-model:checked="includeDeletedValue"
            size="sm"
            class="flex-shrink-0"
            @click.stop
          />
          <Text 
            variant="body-sm" 
            class="cursor-default select-none pointer-events-none"
          >
            {{ t('common.includeDeleted') }}
          </Text>
        </div>

        <!-- Results Count and Actions -->
        <div 
          :class="['flex items-center justify-between', filterColors.text.muted, showDeletedToggle ? 'mt-2' : 'mt-3']"
          role="status"
          aria-live="polite"
        >
          <Text variant="caption">
            {{ customResultMessage 
              ? customResultMessage
              : includeDeleted 
                ? t('reports.filter.showingCountWithDeleted', { 
                    filtered: filteredCount, 
                    total: totalCount, 
                    section: title.toLowerCase() 
                  })
                : t('reports.filter.showingCount', { 
                    filtered: filteredCount, 
                    total: totalCount, 
                    section: title.toLowerCase() 
                  })
            }}
          </Text>
          
          <div class="flex items-center gap-2">
            <HLBadge
              v-if="hasActiveFilters"
              id="active-filters-badge"
              color="blue"
              size="sm"
            >
              {{ t('reports.filter.activeFilters', { count: activeFilterCount }) }}
            </HLBadge>
            
            <HLButton
              v-if="hasActiveFilters"
              @click="handleClear"
              variant="link"
              size="md"
              :aria-label="t('reports.filter.clearAll')"
            >
              {{ t('reports.filter.clearAll') }}
            </HLButton>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import Heading from '@/components/ui/Heading.vue'
import Text from '@/components/ui/Text.vue'
import { HLButton, HLBadge, HLCheckbox } from '@platform-ui/highrise'
import { HLInput } from '@platform-ui/highrise'
import Icon from '@/components/ui/Icon.vue'
import { t } from '@/i18n/en-us'
import { REPORT_COLORS } from '@/constants/uiColors'

// Props
const props = defineProps({
  title: {
    type: String,
    default: ''
  },
  showHeader: {
    type: Boolean,
    default: true
  },
  expanded: {
    type: Boolean,
    default: false
  },
  showSearch: {
    type: Boolean,
    default: true
  },
  showExport: {
    type: Boolean,
    default: true
  },
  search: {
    type: String,
    default: ''
  },
  // Support kebab-case prop name (:search-value maps to searchValue)
  searchValue: {
    type: String,
    default: ''
  },
  searchPlaceholder: {
    type: String,
    default: ''
  },
  filteredCount: {
    type: Number,
    required: true
  },
  totalCount: {
    type: Number,
    required: true
  },
  activeFilterCount: {
    type: Number,
    default: 0
  },
  hasActiveFilters: {
    type: Boolean,
    default: false
  },
  gridColumns: {
    type: Object,
    default: () => ({ base: 1, md: 2, lg: 3 })
  },
  exportText: {
    type: String,
    default: () => t('reports.exportReport')
  },
  exportVariant: {
    type: String,
    default: 'primary'
  },
  toggleVariant: {
    type: String,
    default: 'outline'
  },
  showDeletedToggle: {
    type: Boolean,
    default: false
  },
  includeDeleted: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  customResultMessage: {
    type: String,
    default: ''
  },
  actionButtons: {
    type: Array,
    default: () => [],
    validator: (buttons) => {
      return buttons.every(button => 
        button.label && typeof button.onClick === 'function'
      )
    }
  },
  showFilters: {
    type: Boolean,
    default: true
  }
})

// Emits
const emit = defineEmits(['toggle', 'clear', 'update:search', 'export', 'update:includeDeleted'])

// Internal ref for search value to enable v-model binding
const searchValue = ref(props.searchValue || props.search || '')

// Watch for prop changes and update internal ref
watch(() => props.searchValue || props.search, (newValue) => {
  if (newValue !== searchValue.value) {
    searchValue.value = newValue || ''
  }
}, { immediate: true })

// Watch internal ref changes and emit to parent
watch(searchValue, (newValue) => {
  emit('update:search', newValue || '')
})

// Unique ID for accessibility
const uniqueId = computed(() => `${props.title.toLowerCase().replace(/\s+/g, '-')}-${Math.random().toString(36).substr(2, 9)}`)

// Computed
const filterColors = computed(() => REPORT_COLORS.filter)

const gridClasses = computed(() => {
  const { base, md, lg } = props.gridColumns
  return `grid grid-cols-${base} md:grid-cols-${md} lg:grid-cols-${lg} gap-3`
})

const searchColSpan = computed(() => {
  const { md, lg } = props.gridColumns
  return `md:col-span-${md} lg:col-span-${lg}`
})

// Default button props to match the toggle button style
const defaultActionButtonProps = {
  variant: 'outline',
  size: 'md'
}

// Helper to merge button props with defaults
const mergeButton = (button) => {
  return {
    ...defaultActionButtonProps,
    ...button,
    variant: button.variant || defaultActionButtonProps.variant,
    size: button.size || defaultActionButtonProps.size
  }
}

// Computed for checkbox binding
const includeDeletedValue = computed({
  get: () => props.includeDeleted,
  set: (val) => {
    emit('update:includeDeleted', val)
  }
})

// Methods
const handleToggle = () => {
  emit('toggle')
}

const handleClear = () => {
  emit('clear')
}

const handleExport = () => {
  emit('export')
}
</script>

<style scoped>
/* Filter panel slide transition */
.filter-slide-enter-active,
.filter-slide-leave-active {
  transition: all 0.3s ease;
}

.filter-slide-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.filter-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Override Highrise button CSS variables for primary color - use same variables as working button */
.export-btn {
  --n-color: var(--primary-600) !important;
  --n-color-hover: var(--primary-800) !important;
  --n-color-pressed: var(--primary-900) !important;
  --n-color-focus: var(--primary-800) !important;
  --n-color-disabled: var(--primary-200) !important;
  --n-ripple-color: var(--primary-600) !important;
  --n-border: 1px solid var(--primary-600) !important;
  --n-border-hover: 1px solid var(--primary-800) !important;
  --n-border-pressed: 1px solid var(--primary-900) !important;
  --n-border-focus: 1px solid var(--primary-800) !important;
  --n-border-disabled: 1px solid var(--primary-200) !important;
  --n-outline-focus: 4px solid var(--primary-100) !important;
}
</style>

